import {
    addCustomEventListener,
    type BlockDefinition,
    type RecordItem,
    blockIsMapped,
    isDetailsBlock,
    modalCurrentRecordEvent,
} from "@softr-io/blocks-sdk";
import { BlockProps } from "@softr-io/blocks-sdk/react";
import { isInModal } from "@softr/utils";
import { useEffect, useRef, useState } from "react";
import { useDeepMemo } from "./useDeepMemo";
import { isEqual, pickBy } from "lodash-es";
import { findFirstMappedDetailsBlock } from "./findFirstMappedDetailsBlock";
import { isSamePageFetchTarget } from "./fetchPageDefinition";

/**
 * The "Current record" refers to the "First details block's record".
 * In theory, this is the current page's main record.
 *
 * In practice, the first details block might be hidden by conditional filters,
 * so we fall back to the next details block connected to the same datasource.
 * Additionally, we merge in all the fields from the other details blocks which
 * fetch the same record id.
 */
export function useCurrentRecord(definitions: readonly BlockDefinition[]): BlockProps["currentRecord"] {
    const firstDetailsBlock = findFirstMappedDetailsBlock(definitions);
    const allDetailsBlocksConnectedToTheSameDatasource = useDeepMemo(
        firstDetailsBlock
            ? definitions.filter(
                  (def) =>
                      blockIsMapped(def) &&
                      isDetailsBlock(def) &&
                      areConnectedToTheSameDatasource(def, firstDetailsBlock),
              )
            : [],
    );
    const { recordsByBlockId, recordLoadedByBlockId } = useDetailsRecordsByBlockId(definitions);
    const relevantRecords = allDetailsBlocksConnectedToTheSameDatasource
        .map((def) => recordsByBlockId[def.id])
        .filter((record) => !!record);
    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    let merged = relevantRecords.at(0);
    for (const record of relevantRecords.slice(1)) {
        if (!merged) break;
        if (record.id !== merged.id) continue;
        merged = {
            ...merged,
            fields: {
                ...merged.fields,
                ...record.fields,
            },
        };
    }
    const anyRelevantRecordIsLoading = allDetailsBlocksConnectedToTheSameDatasource.some(
        (def) => recordLoadedByBlockId[def.id] !== true,
    );

    const currentRecord = useDeepMemo({
        isLoading: anyRelevantRecordIsLoading,
        data: merged ?? null,
        blockId: firstDetailsBlock?.id ?? null,
    });

    // Forward the record to the parent, its AI Agent panel.
    useEffect(() => {
        if (!isInModal() || window.parent === window) return;
        window.parent.dispatchEvent(
            modalCurrentRecordEvent(
                currentRecord.data ? { data: currentRecord.data, blockId: currentRecord.blockId ?? null } : null,
            ),
        );
    }, [currentRecord]);

    return currentRecord;
}

type DetailsRecordByBlockId = {
    readonly [key: string]: RecordItem | null;
};

const EMPTY_OBJECT = {} as const;
function useDetailsRecordsByBlockId(definitions: readonly BlockDefinition[]) {
    const [recordsByBlockId, setRecordsByBlockId] = useState<DetailsRecordByBlockId>(EMPTY_OBJECT);
    const [recordLoadedByBlockId, setRecordLoadedByBlockId] = useState<Record<string, boolean>>(EMPTY_OBJECT);

    const detailsBlockKeys = useDeepMemo(
        definitions
            .filter(blockIsMapped)
            .filter(isDetailsBlock)
            .map((it) => ({ id: it.id, hrid: it.hrid })),
    );
    const lastHrefRef = useRef(window.location.href);
    useEffect(() => {
        const isSamePage = isSamePageFetchTarget(lastHrefRef.current, window.location.href);
        lastHrefRef.current = window.location.href;
        if (isSamePage) {
            // Blocks still on the page only dispatch again when their data changes, so keep their records.
            const ids = new Set(detailsBlockKeys.map((it) => it.id));
            setRecordsByBlockId((prev) => pickBy(prev, (_, id) => ids.has(id)));
            setRecordLoadedByBlockId((prev) => pickBy(prev, (_, id) => ids.has(id)));
        } else {
            setRecordsByBlockId(EMPTY_OBJECT);
            setRecordLoadedByBlockId(EMPTY_OBJECT);
        }
        const abortController = new AbortController();

        // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
        const timeoutIds: NodeJS.Timeout[] = [];

        for (const { id, hrid } of detailsBlockKeys) {
            let loaded = false;
            addCustomEventListener(
                `get-record-${hrid}`,
                (event) => {
                    // In `dispatchEvent` we dispatch to both window and window.parent
                    // when we're inside the modal iframe.
                    // We don't want the modal's "currentRecord"
                    // to be treated as the parent window's current record
                    if ("fromChildFrame" in event && event.fromChildFrame) return;

                    loaded = true;
                    // when there is no record, `event.detail` is an empty object instead of `RecordItem`
                    const record = "id" in event.detail ? event.detail : null;
                    setRecordsByBlockId((prev) => ({
                        ...prev,
                        [id]: record,
                    }));
                    setRecordLoadedByBlockId((prev) => ({
                        ...prev,
                        [id]: true,
                    }));
                },
                { signal: abortController.signal },
            );

            const timeoutId = setTimeout(() => {
                if (!loaded) {
                    setRecordsByBlockId((prev) => (prev[id] === undefined ? { ...prev, [id]: null } : prev));
                    setRecordLoadedByBlockId((prev) => ({
                        ...prev,
                        [id]: true,
                    }));
                }
            }, 5000);
            timeoutIds.push(timeoutId);
        }
        return () => {
            abortController.abort();
            timeoutIds.forEach(clearTimeout);
        };
    }, [detailsBlockKeys]);
    return {
        recordsByBlockId,
        recordLoadedByBlockId,
    };
}
function areConnectedToTheSameDatasource(defA: BlockDefinition, defB: BlockDefinition): boolean {
    const dsA = defA.collection.dataSources.find((ds) => ds.referenceType === "BLOCK");
    const dsB = defB.collection.dataSources.find((ds) => ds.referenceType === "BLOCK");
    if (!dsA || !dsB) return false;
    return isEqual(dsA.resourceIdentifier, dsB.resourceIdentifier);
}
